import React from "react";
import { useLang } from "@/i18n";

/**
 * Renders a single question based on its type. Bilingual labels.
 * value -> answers map keyed by question id (handled by parent)
 */
const QuestionRenderer = ({ question, value, onChange }) => {
  const { t, pickI18n } = useLang();
  const label = pickI18n(question, "label") || question.label_en;
  const help = pickI18n(question, "help");

  const id = `q-${question.id}`;
  const isSensitive = (question.sensitive_tags || []).length > 0;

  const baseLabel = (
    <label htmlFor={id} className="tsw-label flex items-center gap-2">
      <span>{label}{question.required && <span className="text-rose-500"> *</span>}</span>
      {isSensitive && (
        <span className="tsw-chip bg-amber-50 text-amber-700 border border-amber-200" title="Research-only sensitive data">
          {t("phase2.sensitiveBadgeShort")}
        </span>
      )}
    </label>
  );

  const opts = question.options || [];

  // Normalise legacy / alternate type strings emitted by the import pipeline
  // so the UI renders the right widget regardless of the source format.
  // Accepts: multi_choice | multiple_choice | checkbox  -> multi
  //          single_choice | radio                       -> single
  //          long_text | textarea                        -> textarea
  //          short_text | text                           -> input
  const RAW = (question.type || "").toLowerCase();
  const normalisedType =
    ({
      multi_choice: "multi_choice",
      multiple_choice: "multi_choice",
      checkbox: "multi_choice",
      single_choice: "single_choice",
      radio: "single_choice",
      long_text: "textarea",
      textarea: "textarea",
      short_text: "text",
      text: "text",
    }[RAW]) || RAW;

  // Defensive guard: if a choice question came in with zero options the
  // renderer must NEVER fall back to a text input (that's the bug QA found).
  // Show a clearly-labelled admin warning so the data issue is visible
  // rather than silently masked as a text answer.
  if ((normalisedType === "multi_choice" || normalisedType === "single_choice") && opts.length === 0) {
    return (
      <div data-testid={`question-${question.id}`}>
        {baseLabel}
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900" data-testid={`q-options-missing-${question.id}`}>
          {t("phase2.optionsMissingWarning")}
        </div>
      </div>
    );
  }

  switch (normalisedType) {
    case "textarea":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          {help && <p className="text-xs text-slate-500 mb-1.5">{help}</p>}
          <textarea
            id={id}
            className="tsw-input min-h-[110px]"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`q-input-${question.id}`}
          />
        </div>
      );
    case "single_choice":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            {opts.map((o) => (
              <label key={o.value} className={`cursor-pointer rounded-lg border px-3 py-2.5 text-sm transition ${value === o.value ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name={id} className="sr-only" checked={value === o.value} onChange={() => onChange(o.value)} data-testid={`q-opt-${question.id}-${o.value}`} />
                {pickI18n(o, "label") || o.label_en}
              </label>
            ))}
          </div>
        </div>
      );
    case "multi_choice":
      const arr = Array.isArray(value) ? value : [];
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            {opts.map((o) => {
              const checked = arr.includes(o.value);
              return (
                <label key={o.value} className={`cursor-pointer rounded-lg border px-3 py-2.5 text-sm transition ${checked ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:bg-slate-50"}`}>
                  <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => {
                    const next = e.target.checked ? [...arr, o.value] : arr.filter((v) => v !== o.value);
                    onChange(next);
                  }} data-testid={`q-opt-${question.id}-${o.value}`} />
                  {pickI18n(o, "label") || o.label_en}
                </label>
              );
            })}
          </div>
        </div>
      );
    case "scale":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <div className="flex gap-2 mt-1">
            {[0,1,2,3,4,5].map((n) => (
              <button
                type="button"
                key={n}
                onClick={() => onChange(n)}
                className={`w-10 h-10 rounded-full border text-sm font-semibold transition ${value === n ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-700 hover:border-emerald-300"}`}
                data-testid={`q-scale-${question.id}-${n}`}
              >{n}</button>
            ))}
          </div>
        </div>
      );
    case "boolean":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <div className="flex gap-2 mt-1">
            {[true, false].map((b) => (
              <button key={String(b)} type="button" onClick={() => onChange(b)} className={`px-4 py-2 rounded-full border text-sm ${value === b ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-700"}`} data-testid={`q-bool-${question.id}-${b}`}>
                {b ? t("common.yes") : t("common.no")}
              </button>
            ))}
          </div>
        </div>
      );
    case "number":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <input id={id} type="number" className="tsw-input" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={`q-input-${question.id}`} />
        </div>
      );
    case "email":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <input id={id} type="email" className="tsw-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-input-${question.id}`} />
        </div>
      );
    case "date":
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <input id={id} type="date" className="tsw-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-input-${question.id}`} />
        </div>
      );
    default:
      return (
        <div data-testid={`question-${question.id}`}>
          {baseLabel}
          <input id={id} type="text" className="tsw-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`q-input-${question.id}`} />
        </div>
      );
  }
};

export default QuestionRenderer;
